import { useEffect, useState, useMemo } from "react";
import { Trash2 } from "lucide-react";

interface FieldEditPopoverProps {
  /** Anchor box in image natural pixels. */
  anchor: { x: number; y: number; width: number; height: number };
  /** Image natural width/height — used to decide placement. */
  imageNatural: { width: number; height: number };
  fieldName: string;
  value: string;
  options?: string[];
  onNameChange?: (next: string) => void;
  onValueChange: (next: string) => void;
  onClear: () => void;
  onSave: () => void;
  onClose: () => void;
  onDelete?: () => void;
  /** Disable interaction while a mutation is in flight. */
  busy?: boolean;
}

/**
 * Compact editor popover anchored to a highlighted bbox on the document.
 * Picks a side that doesn't overlap the bbox (preference order: below →
 * right → above → left) and offsets by a fixed gap so both the bbox
 * border and the connecting-line endpoint remain visible.
 */
export function FieldEditPopover({
  anchor,
  imageNatural,
  fieldName,
  value,
  onNameChange,
  onValueChange,
  onClear,
  onSave,
  onClose,
  onDelete,
  busy = false,
  options = [],
}: FieldEditPopoverProps) {
  const [localName, setLocalName] = useState(fieldName);
  const [localValue, setLocalValue] = useState(value);
  const [isNameDropdownOpen, setIsNameDropdownOpen] = useState(false);
  const [nameFilter, setNameFilter] = useState("");

  // Re-sync when a different field becomes active.
  useEffect(() => setLocalName(fieldName), [fieldName]);
  useEffect(() => setLocalValue(value), [value]);

  const filteredOptions = useMemo(() => {
    if (!nameFilter) return options;
    const q = nameFilter.toLowerCase();
    return options.filter((opt) => opt.toLowerCase().includes(q));
  }, [options, nameFilter]);

  const naturalW = Math.max(imageNatural.width, 1);
  const naturalH = Math.max(imageNatural.height, 1);

  // Approximate popover footprint expressed in NATURAL image pixels.
  // The popover renders inside the same scaled container as the image, so
  // sizes in % of natural dims map directly to its rendered size.
  const popoverWPct = 18; // ~18% of image width
  const popoverHPct = 22; // ~22% of image height
  const gapPct = 1.5;

  const bboxLeftPct = (anchor.x / naturalW) * 100;
  const bboxRightPct = ((anchor.x + anchor.width) / naturalW) * 100;
  const bboxTopPct = (anchor.y / naturalH) * 100;
  const bboxBottomPct = ((anchor.y + anchor.height) / naturalH) * 100;

  // Choose placement in priority order: below → right → above → left.
  let topPct: number;
  let leftPct: number;
  if (bboxBottomPct + gapPct + popoverHPct <= 100) {
    // Below
    topPct = bboxBottomPct + gapPct;
    leftPct = clamp(
      bboxLeftPct + (anchor.width / naturalW) * 50 - popoverWPct / 2,
      gapPct,
      100 - popoverWPct - gapPct,
    );
  } else if (bboxRightPct + gapPct + popoverWPct <= 100) {
    // Right
    leftPct = bboxRightPct + gapPct;
    topPct = clamp(bboxTopPct, gapPct, 100 - popoverHPct - gapPct);
  } else if (bboxTopPct - gapPct - popoverHPct >= 0) {
    // Above
    topPct = bboxTopPct - gapPct - popoverHPct;
    leftPct = clamp(
      bboxLeftPct + (anchor.width / naturalW) * 50 - popoverWPct / 2,
      gapPct,
      100 - popoverWPct - gapPct,
    );
  } else {
    // Left (last resort)
    leftPct = Math.max(gapPct, bboxLeftPct - gapPct - popoverWPct);
    topPct = clamp(bboxTopPct, gapPct, 100 - popoverHPct - gapPct);
  }

  const popoverStyle: React.CSSProperties = {
    position: "absolute",
    top: `${topPct}%`,
    left: `${leftPct}%`,
    width: `${popoverWPct}%`,
    minWidth: "13rem",
    maxWidth: "16rem",
  };

  return (
    <div
      style={popoverStyle}
      className="z-30 rounded-xl border border-gray-200 bg-white p-3 shadow-xl ring-1 ring-black/5"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="space-y-2.5">
        <div className="relative">
          <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Name
          </label>
          <div
            className="flex w-full cursor-pointer items-center justify-between rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-900 transition-colors hover:bg-gray-50 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            onClick={() => setIsNameDropdownOpen(!isNameDropdownOpen)}
          >
            <span className="truncate">{localName || "Select field name..."}</span>
            <svg
              className={`h-3 w-3 text-gray-400 transition-transform ${isNameDropdownOpen ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {isNameDropdownOpen && (
            <div className="absolute left-0 top-full z-[110] mt-1 w-full rounded border border-gray-200 bg-white shadow-xl">
              <div className="border-b border-gray-100 p-1.5">
                <input
                  autoFocus
                  type="text"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Search..."
                  className="w-full rounded border border-gray-200 px-2 py-1 text-[11px] focus:border-blue-500 focus:outline-none"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <ul className="max-h-32 overflow-y-auto py-0.5">
                {filteredOptions.length === 0 ? (
                  <li className="px-2 py-1.5 text-[10px] text-gray-500">No results</li>
                ) : (
                  filteredOptions.map((opt) => (
                    <li key={opt}>
                      <button
                        type="button"
                        onClick={() => {
                          setLocalName(opt);
                          onNameChange?.(opt);
                          setIsNameDropdownOpen(false);
                        }}
                        className={`flex w-full items-center justify-between px-2 py-1.5 text-left text-[11px] transition-colors hover:bg-blue-50 ${
                          localName === opt ? "bg-blue-50 font-semibold text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {opt}
                        {localName === opt && (
                          <svg className="h-3 w-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>

        <div>
          <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Content
          </label>
          <input
            type="text"
            value={localValue}
            onChange={(e) => {
              setLocalValue(e.target.value);
              onValueChange(e.target.value);
            }}
            placeholder="Value"
            autoFocus
            className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="mt-2.5 flex items-center gap-1.5">
        {onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            title="Delete field"
            className="rounded-md p-1 text-gray-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        ) : null}

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 hover:bg-gray-100 disabled:opacity-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => {
              onClear();
              setLocalValue("");
            }}
            disabled={busy}
            className="rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={busy}
            className="rounded-md bg-blue-500 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm hover:bg-blue-600 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
